import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  signal,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
} from '@angular/core';

import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { BottomSheetVariant, BottomSheetSize } from '../models/bottom-sheet.model';

const bottomSheetVariants = cva({
  base: [
    'fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ease-out',
    'backdrop-blur-xl border-t transform-gpu',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-70 before:pointer-events-none',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'bg-linear-to-t from-slate-800/92 via-slate-900/94 to-blue-950/88',
        'border-blue-200/14',
        'shadow-2xl shadow-slate-950/36',
      ].join(' '),
      elevated: [
        'bg-linear-to-t from-slate-800/94 via-slate-900/95 to-blue-950/90',
        'border-blue-200/16',
        'shadow-2xl shadow-slate-950/40',
      ].join(' '),
      premium: [
        'bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.14),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.08),transparent_40%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border-[rgba(146,176,218,0.18)]',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_60px_rgba(2,8,23,0.42),0_0_60px_rgba(37,99,235,0.08)]',
      ].join(' '),
      minimal: [
        'bg-linear-to-t from-slate-800/86 via-slate-900/90 to-blue-950/84',
        'border-blue-200/10',
        'shadow-xl shadow-slate-950/24',
      ].join(' '),
    },
    size: {
      sm: 'max-h-[30vh]',
      md: 'max-h-[50vh]',
      lg: 'max-h-[70vh]',
      xl: 'max-h-[90vh]',
      auto: 'max-h-[80vh]',
    },
    open: {
      true: 'translate-y-0',
      false: 'translate-y-full',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    open: 'false',
  },
});

const backdropVariants = cva({
  base: ['fixed inset-0 z-40 transition-all duration-300 ease-out', 'backdrop-blur-sm'].join(' '),
  variants: {
    variant: {
      default: 'bg-[linear-gradient(180deg,rgba(2,8,23,0.52)_0%,rgba(7,18,37,0.42)_100%)]',
      elevated: 'bg-[linear-gradient(180deg,rgba(2,8,23,0.58)_0%,rgba(7,18,37,0.48)_100%)]',
      premium:
        'bg-[radial-gradient(circle_at_right,rgba(37,99,235,0.14),transparent_28%),linear-gradient(180deg,rgba(2,8,23,0.62)_0%,rgba(7,18,37,0.5)_100%)]',
      minimal: 'bg-[linear-gradient(180deg,rgba(2,8,23,0.46)_0%,rgba(7,18,37,0.38)_100%)]',
    },
    visible: {
      true: 'opacity-100 pointer-events-auto',
      false: 'opacity-0 pointer-events-none',
    },
  },
  defaultVariants: {
    variant: 'default',
    visible: 'false',
  },
});

const contentVariants = cva({
  base: ['relative z-10 h-full flex flex-col overflow-hidden rounded-t-3xl'].join(' '),
  variants: {
    variant: {
      default: '',
      elevated: '',
      premium: '',
      minimal: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

@Component({
  selector: 'ui-bottom-sheet-glass',
  standalone: true,
  imports: [IconGlassComponent],
  templateUrl: './bottom-sheet-glass.component.html',
})
export class BottomSheetGlassComponent implements OnInit, OnDestroy {
  @ViewChild('bottomSheetElement') bottomSheetElement!: ElementRef;

  @Input() variant: BottomSheetVariant = 'default';
  @Input() size: BottomSheetSize = 'md';
  @Input() showBackdrop: boolean = true;
  @Input() closeOnBackdrop: boolean = true;
  @Input() closeOnEscape: boolean = true;
  @Input() showDragHandle: boolean = true;
  @Input() hasHeader: boolean = true;
  @Input() hasFooter: boolean = false;
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() showCloseButton: boolean = true;
  @Input() ariaLabel: string = 'Bottom sheet';
  @Input() themeSection: ThemeSection = 'global';

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() backdropClick = new EventEmitter<void>();

  public isOpen = signal(false);

  protected computedBottomSheetClasses = computed(() => {
    const openState = this.isOpen() ? 'true' : 'false';
    return bottomSheetVariants({
      variant: this.variant,
      size: this.size,
      open: openState,
    });
  });

  protected computedBackdropClasses = computed(() => {
    return backdropVariants({
      variant: this.variant,
      visible: this.isOpen() ? 'true' : 'false',
    });
  });

  protected computedContentClasses = computed(() => {
    return contentVariants({
      variant: this.variant,
    });
  });

  ngOnInit(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this.handleGlobalKeyDown.bind(this));
    }
  }

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.handleGlobalKeyDown.bind(this));
    }
  }

  open(): void {
    this.isOpen.set(true);
    this.opened.emit();

    // Focus management
    setTimeout(() => {
      if (this.bottomSheetElement?.nativeElement) {
        this.bottomSheetElement.nativeElement.focus();
      }
    }, 300);
  }

  close(): void {
    if (this.isOpen()) {
      this.isOpen.set(false);
      this.closed.emit();
    }
  }

  handleCloseClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  public handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.closeOnEscape) {
      event.preventDefault();
      this.close();
    }
  }

  private handleGlobalKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.closeOnEscape && this.isOpen()) {
      this.close();
    }
  }
}

/**
 * Usage Examples:
 *
 * Basic bottom sheet:
 * <ui-bottom-sheet-glass
 *   title="Orders for Pickup"
 *   subtitle="Ready for collection"
 *   #ordersSheet
 * >
 *   <div class="p-6">
 *     <ui-list [items]="orders"></ui-list>
 *   </div>
 * </ui-bottom-sheet-glass>
 *
 * Premium e-commerce bottom sheet:
 * <ui-bottom-sheet-glass
 *   title="Shopping Cart"
 *   subtitle="3 items"
 *   variant="premium"
 *   size="lg"
 *   [hasFooter]="true"
 *   #cartSheet
 * >
 *   <div class="p-6">
 *     <!-- Cart items -->
 *   </div>
 *
 *   <div slot="footer" class="flex justify-between items-center">
 *     <span class="text-lg font-semibold">Total: $299.99</span>
 *     <ui-button-glass variant="primary">Checkout</button>
 *   </div>
 * </ui-bottom-sheet-glass>
 *
 * Minimal notification sheet:
 * <ui-bottom-sheet-glass
 *   title="Notifications"
 *   variant="minimal"
 *   size="sm"
 *   [closeOnBackdrop]="true"
 *   [showDragHandle]="false"
 * >
 *   <div class="p-6">
 *     <!-- Notification list -->
 *   </div>
 * </ui-bottom-sheet-glass>
 *
 * Control from template:
 * <ui-button-glass (click)="ordersSheet.open()">View Orders</button>
 * <ui-button-glass (click)="cartSheet.toggle()">Toggle Cart</button>
 *
 * Different variants:
 * <ui-bottom-sheet-glass variant="elevated" title="Elevated Sheet"></ui-bottom-sheet-glass>
 * <ui-bottom-sheet-glass variant="minimal" title="Minimal Sheet"></ui-bottom-sheet-glass>
 * <ui-bottom-sheet-glass variant="premium" title="Premium Sheet"></ui-bottom-sheet-glass>
 */
